/**
 * Per-day-of-week price-override editor (DNKADM-0086 U1) — manages the `weekly-prices`
 * rows under ONE variation (DNKAPI-0168). It renders BESIDE {@link StaffPriceEditor} in
 * the variation's expand row (a sibling sub-section; the staff-price editor is unmoved).
 *
 * A 7-row grid, **Sunday→Saturday** (`dayOfWeek` 0..6 — the hours/schedules convention).
 * Each row carries a price input pre-filled with the day's override when one is set;
 * empty means the day **uses the variation's base price** (shown as the input's
 * placeholder/hint). Saving a day upserts via `putWeeklyPrice`; clearing a day removes
 * the override via `deleteWeeklyPrice` (the day reverts to base). The grid refetches via
 * `getWeeklyPrices` after each write.
 *
 * Money-only — the variation's `creditCost` is unaffected (§Non-Goals). Client
 * validation mirrors the server (`>= 0`, ≤ 2 decimals); a `400` surfaces inline on the
 * row, anything else raises a toast (the panel's error idiom, via `classifyFormError`).
 * Precedence (`Date > Staff > Day-of-week > Base`) is never resolved client-side.
 */
import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { IconAction } from '@/components/IconAction';
import { Icon } from '@/theme';
import {
  deleteWeeklyPrice,
  getWeeklyPrices,
  putWeeklyPrice,
  type WeeklyPrice,
} from '@/api/services';
import { formatMoney } from '@/utils/v2Format';
import type { ShowToast } from '@/components/toastContext';
import { classifyFormError } from '@/api/screenError';

export interface WeeklyPriceEditorProps {
  serviceId: string;
  variationId: string;
  /** The variation's base price — shown as each empty day's "uses base" hint. */
  basePrice: number;
  /** The currency code, or null while it loads. */
  currency: string | null;
  showToast: ShowToast;
  /** Fired after every successful price write (DNKADM-0304 U3) — see StaffPriceEditor. */
  onChanged?: () => void;
}

/** Full day labels, indexed by `dayOfWeek` (0=Sun … 6=Sat). */
const DAY_LABELS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/**
 * Parse a price text field to a finite number ≥ 0 with ≤ 2 decimals (mirrors the
 * server), or null when invalid. A blank field is handled separately by the caller (it
 * means "clear/use base"), so this only judges a non-blank entry.
 */
function parsePrice(text: string): number | null {
  const trimmed = text.trim();
  // `>= 0`, ≤ 2 decimals — no leading sign, at most two fraction digits.
  if (!/^\d+(\.\d{1,2})?$/.test(trimmed)) return null;
  const n = Number(trimmed);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

export function WeeklyPriceEditor({
  serviceId,
  variationId,
  basePrice,
  currency,
  showToast,
  onChanged,
}: WeeklyPriceEditorProps) {
  const [rows, setRows] = useState<WeeklyPrice[]>([]);
  const [loadError, setLoadError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  // The editable text per day (0..6), seeded from the loaded overrides on each load.
  const [drafts, setDrafts] = useState<Record<number, string>>({});
  const [rowError, setRowError] = useState<Record<number, string>>({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const loaded = await getWeeklyPrices(serviceId, variationId);
        if (cancelled) return;
        setRows(loaded);
        setLoadError(false);
        // Seed each day's draft from its override; days with no override stay blank.
        const seeded: Record<number, string> = {};
        for (const r of loaded) seeded[r.dayOfWeek] = String(r.price);
        setDrafts(seeded);
        setRowError({});
      } catch {
        if (cancelled) return;
        setLoadError(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [serviceId, variationId, reloadKey]);

  // The persisted override price per day (so a row knows whether a Clear applies).
  const persisted = useMemo(() => {
    const map = new Map<number, number>();
    for (const r of rows) map.set(r.dayOfWeek, r.price);
    return map;
  }, [rows]);

  const basePlaceholder = currency ? formatMoney(basePrice, currency) : basePrice.toFixed(2);

  function setDraft(day: number, value: string) {
    setDrafts((prev) => ({ ...prev, [day]: value }));
  }

  async function handleSave(day: number) {
    setRowError((prev) => ({ ...prev, [day]: '' }));
    const text = drafts[day] ?? '';
    if (text.trim() === '') {
      setRowError((prev) => ({ ...prev, [day]: 'Enter a price, or clear to use the base price.' }));
      return;
    }
    const price = parsePrice(text);
    if (price === null) {
      setRowError((prev) => ({
        ...prev,
        [day]: 'Enter a valid price (0 or more, up to 2 decimals).',
      }));
      return;
    }
    setBusy(true);
    try {
      await putWeeklyPrice(serviceId, variationId, day, price);
      setReloadKey((k) => k + 1);
      onChanged?.();
      showToast(`${DAY_LABELS[day]} price saved`, 'success');
    } catch (err) {
      const view = classifyFormError(err, 'Couldn’t save the day price.');
      if (view.kind === 'inline') setRowError((prev) => ({ ...prev, [day]: view.message }));
      else showToast(view.message, 'error');
    } finally {
      setBusy(false);
    }
  }

  async function handleClear(day: number) {
    setRowError((prev) => ({ ...prev, [day]: '' }));
    setBusy(true);
    try {
      await deleteWeeklyPrice(serviceId, variationId, day);
      setReloadKey((k) => k + 1);
      onChanged?.();
      showToast(`${DAY_LABELS[day]} price removed`, 'success');
    } catch (err) {
      const view = classifyFormError(err, 'Couldn’t remove the day price.');
      showToast(view.message, 'error');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Box sx={{ mt: 1 }}>
      <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
        Day-of-week prices
      </Typography>
      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1 }}>
        Override this variation’s price by day of week (e.g. weekends cost more). An empty
        day uses the base price ({basePlaceholder}); credits are unchanged.
      </Typography>

      {loadError && (
        <Alert
          severity="error"
          sx={{ mb: 1, py: 0.5 }}
          action={
            <Button color="inherit" size="small" onClick={() => setReloadKey((k) => k + 1)}>
              Retry
            </Button>
          }
        >
          Couldn’t load the day-of-week prices.
        </Alert>
      )}

      <Stack spacing={1} sx={{ maxWidth: 420 }}>
        {DAY_LABELS.map((label, day) => {
          const hasOverride = persisted.has(day);
          const error = rowError[day];
          return (
            <Box key={day}>
              <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
                <Typography variant="body2" sx={{ width: 96, flexShrink: 0 }}>
                  {label}
                </Typography>
                <TextField
                  type="number"
                  size="small"
                  value={drafts[day] ?? ''}
                  placeholder={basePlaceholder}
                  onChange={(e) => setDraft(day, e.target.value)}
                  disabled={busy}
                  error={Boolean(error)}
                  sx={{ width: 150 }}
                  slotProps={{
                    htmlInput: {
                      'aria-label': `${label} price`,
                      min: 0,
                      step: '0.01',
                      inputMode: 'decimal',
                    },
                    ...(currency
                      ? { input: { startAdornment: <InputAdornment position="start">{currency}</InputAdornment> } }
                      : {}),
                  }}
                />
                <Button
                  variant="outlined"
                  size="small"
                  aria-label={`Save ${label} price`}
                  onClick={() => void handleSave(day)}
                  disabled={busy}
                >
                  Save
                </Button>
                <IconAction
                  size="small"
                  label={`Clear ${label} price override`}
                  onClick={() => void handleClear(day)}
                  disabled={busy || !hasOverride}
                >
                  <Icon name="delete" />
                </IconAction>
              </Stack>
              {error && (
                <Typography role="alert" sx={{ mt: 0.25, ml: '108px', fontSize: 12, color: 'error.main' }}>
                  {error}
                </Typography>
              )}
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
}
